/**
 * Who may see and do what on Telephony → Providers and Capacity (14 Oct 2026).
 *
 * The backend is the authority: for a non-master it returns only the caller's own trunks,
 * the numbers their tree holds, and their own capacity rows, and it refuses everything
 * else. These rules only keep the portal from offering buttons the server will refuse,
 * and name a platform trunk "Platform" wherever a row still points at one.
 */
import type { Provider, ProviderNumber, BusinessProviderConfig } from 'api/services/providers.service';
import type { CapacityLimit } from 'api/services/capacityLimits.service';

export const PLATFORM_LABEL = 'Platform';

export const PLATFORM_CARRIERS_COPY =
  "Your calls run on Green Giraffe's platform carriers, managed by Green Giraffe.";

/** MASTER_ADMIN and its renamed twin PLATFORM_ADMIN: the only tier that sees every trunk. */
export const isPlatformTier = (role?: string | null): boolean =>
  ['master_admin', 'platform_admin'].includes((role || '').toLowerCase());

/** A trunk's name, or "Platform" when the caller cannot see it (master sees an id instead). */
export const trunkLabel = (id: number | null | undefined, providers: Provider[], master: boolean,
  fallbackName?: string | null): string => {
  if (fallbackName) return fallbackName;
  const p = id == null ? undefined : providers.find(x => x.id === id);
  if (p) return p.name;
  return master && id != null ? `#${id}` : PLATFORM_LABEL;
};

/** Numbers can be added to, deleted from and probed on: any trunk for master, own EXTERNAL ones otherwise. */
export const mayManageTrunk = (p: Provider | undefined, master: boolean): boolean =>
  master || (!!p && p.trunk_type === 'EXTERNAL');

/** Trunks a non-master may add numbers to. */
export const manageableTrunks = (providers: Provider[], master: boolean): Provider[] =>
  providers.filter(p => mayManageTrunk(p, master));

/** Delete a number: master always; otherwise only on their own EXTERNAL trunk. */
export const mayDeleteNumber = (n: ProviderNumber, providers: Provider[], master: boolean): boolean =>
  master || mayManageTrunk(providers.find(p => p.id === n.provider_id), master);

/** Editing or deleting a routing row that rides a platform trunk is the platform admin's call. */
export const mayChangeRouting = (c: BusinessProviderConfig, master: boolean): boolean =>
  master || !c.platform_managed;

/** Capacity rows: DERIVED and trunk ceilings are read-only for a non-master. */
export const mayDeleteLimit = (l: CapacityLimit, master: boolean): boolean =>
  l.source !== 'DERIVED' && (master || l.scope !== 'PROVIDER');

// ── Green Giraffe-granted telephony capabilities (14 Oct 2026) ────────────────
//
// "Add provider, capacity, worker — only allowed once allowed": for every provider other
// than the platform root these are OFF until master grants them on Platform → Feature
// Access. The server refuses with 403 (TelephonyAdminGrants.java); these only hide the
// controls. Without ADD_PROVIDER a provider keeps a read-only view of its own trunks;
// without CAPACITY_MANAGE, of its own limits. WORKER_CONFIG hides the page outright
// (navVisibility GRANT_PATHS): worker config is platform-wide, nothing in it is theirs.

export type TelephonyGrant = 'ADD_PROVIDER' | 'CAPACITY_MANAGE' | 'WORKER_CONFIG';

/** Provider administrator roles, both spellings — the only non-master roles a grant can reach. */
export const PROVIDER_ADMIN_ROLES = ['super_admin', 'service_admin', 'provider_owner', 'provider_admin'];

/**
 * May this caller use a granted telephony capability in the current organisation?
 * Master/platform admin always. Otherwise a provider admin whose organisation holds the
 * grant (the root, Green Giraffe, holds all of them — /v1/feature-grants/mine says so).
 * Unknown grants (still loading, or the call failed) hide: the default is off.
 */
export const holdsTelephonyGrant = (role: string | null | undefined, grants: string[] | undefined,
  feature: TelephonyGrant): boolean => {
  const r = (role || '').toLowerCase();
  if (isPlatformTier(r)) return true;
  return PROVIDER_ADMIN_ROLES.includes(r) && !!grants && grants.includes(feature);
};

export const TRUNKS_GRANT_COPY =
  'Managing trunks is enabled by Green Giraffe — contact Green Giraffe. You can view your own trunks.';
export const CAPACITY_GRANT_COPY =
  'Managing capacity limits is enabled by Green Giraffe — contact Green Giraffe. You can view your own limits.';
